"use client"

import React, { useState, useEffect, useMemo } from "react"
import { Controller, FieldPath, FieldValues, Control, ControllerRenderProps, ControllerFieldState } from "react-hook-form"
import { Input } from "@/shared/components/ui/input"
import { Check, ChevronsUpDown, Loader2 } from "lucide-react"
import { cn } from "@/shared/lib/utils"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover"
import FormTextInputWrapper, { InputGroupProps } from "./form-input-wrapper"
import { useReadOnly } from '../../../features/homeowners/context/read-only-context'
import { getVolunteerOrganizationNames } from "@/features/volunteers/api/volunteers.api"

type FormOrganizationInputProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'name' | 'value' | 'onChange' | 'onBlur' | 'ref'> & {
  name: TName
  control: Control<TFieldValues>
  inputGroupProps?: InputGroupProps
}

interface Organization {
  id: number
  name: string
  contactPerson?: string
  phoneNumber?: string
  email?: string
}

// Separate component to handle input state (allows hooks to be called at top level)
function OrganizationInputField<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
  field,
  fieldState,
  organizations,
  isLoading,
  inputGroupProps,
  placeholder,
  disabled,
  className,
  ...rest
}: {
  field: ControllerRenderProps<TFieldValues, TName>
  fieldState: ControllerFieldState
  organizations: Organization[]
  isLoading: boolean
  inputGroupProps?: InputGroupProps
  placeholder?: string
  disabled?: boolean
  className?: string
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'name' | 'value' | 'onChange' | 'onBlur' | 'ref'>) {
  const [inputValue, setInputValue] = useState(field.value || "")
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const showEmptyHighlight =
    !!inputGroupProps?.highlightWhenEmpty && !(String(inputValue ?? "").trim() || String(field.value ?? "").trim())

  // Initialize input value from field value when it changes externally
  useEffect(() => {
    if (field.value !== undefined && field.value !== inputValue) {
      setInputValue(field.value || "")
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field.value])

  // Filter organizations based on input value
  const filteredOrganizations = useMemo(() => {
    if (!inputValue.trim()) return organizations
    
    const searchLower = inputValue.toLowerCase()
    return organizations.filter(org =>
      org.name.toLowerCase().includes(searchLower)
    )
  }, [organizations, inputValue])

  const handleInputChange = (value: string) => {
    setInputValue(value)
    field.onChange(value) // Update form field with typed value
    if (value.trim() && !isDropdownOpen) {
      setIsDropdownOpen(true)
    }
  }

  const handleSelectOrganization = (orgName: string) => {
    setInputValue(orgName)
    field.onChange(orgName) // Update form field with selected organization name
    setIsDropdownOpen(false)
    field.onBlur() // Trigger blur to mark as touched
  }

  const handleInputFocus = () => {
    setIsDropdownOpen(true)
  }

  const handleInputBlur = () => {
    // Don't close on blur - let click outside handle it
    field.onBlur()
  }

  return (
    <FormTextInputWrapper
      inputGroupProps={{
        ...inputGroupProps,
        error: fieldState.error?.message,
        isDirty: fieldState.isDirty,
      }}
    >
      <div className="relative w-full">
        <Popover open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
          <PopoverTrigger asChild>
            <div className="relative">
              <Input
                {...(Object.fromEntries(
                  Object.entries(rest).filter(([key]) => 
                    !['size', 'name', 'value', 'onChange', 'onBlur'].includes(key)
                  )
                ) as any)}
                type="text"
                placeholder={placeholder}
                value={inputValue}
                onChange={(e) => handleInputChange((e.target as HTMLInputElement).value)}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
                disabled={disabled}
                variant={fieldState.error ? "destructive" : "default"}
                className={cn(
                  "w-full",
                  fieldState.error && "border-destructive",
                  showEmptyHighlight && "!border-[var(--color-pizza-600)] focus:!border-[var(--color-pizza-600)] focus:!ring-[var(--color-pizza-600)]/20",
                  className
                )}
                style={showEmptyHighlight ? { backgroundColor: "var(--color-pizza-600)", opacity: 0.3, color: "#000000" } : undefined}
              />
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2"
                onMouseDown={(e) => {
                  // Prevent input blur/focus churn when clicking chevron inside dialog.
                  e.preventDefault()
                }}
                onClick={() => setIsDropdownOpen(true)}
                disabled={disabled}
              >
                <ChevronsUpDown className="h-4 w-4 text-muted-foreground" />
              </button>
            </div>
          </PopoverTrigger>
          <PopoverContent 
            className="w-full p-0" 
            align="start"
            onOpenAutoFocus={(e) => e.preventDefault()}
          >
            <div className="max-h-[300px] overflow-y-auto">
              {isLoading ? (
                <div className="py-6 text-center text-sm text-muted-foreground flex items-center justify-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading organizations...
                </div>
              ) : filteredOrganizations.length === 0 ? (
                <div className="py-6 text-center text-sm text-muted-foreground">
                  {inputValue.trim() 
                    ? "No organizations found. You can type a new name." 
                    : "Start typing to search organizations..."
                  }
                </div>
              ) : (
                <div className="p-1">
                  {filteredOrganizations.map((org) => (
                    <div
                      key={org.id}
                      onClick={() => handleSelectOrganization(org.name)}
                      className={cn(
                        "flex items-center gap-2 px-2 py-2 text-sm rounded-sm cursor-pointer transition-colors",
                        "hover:bg-accent hover:text-accent-foreground",
                        inputValue === org.name && "bg-accent"
                      )}
                    >
                      <Check 
                        className={cn(
                          "h-4 w-4",
                          inputValue === org.name ? "opacity-100 text-primary" : "opacity-0"
                        )} 
                      />
                      <span className="truncate">{org.name}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </FormTextInputWrapper>
  )
}

function FormOrganizationInput<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
  name,
  control,
  inputGroupProps,
  placeholder = "Search or type organization name...",
  disabled = false,
  className,
  ...rest
}: FormOrganizationInputProps<TFieldValues, TName>) {
  const [organizations, setOrganizations] = useState<Organization[]>([])
  const [isLoading, setIsLoading] = useState(false)
  
  // Check read-only context
  const { isReadOnly } = useReadOnly()

  // Fetch organization names from user profiles (distinct organizationName from non-homeowners)
  useEffect(() => {
    const fetchOrganizations = async () => {
      setIsLoading(true)
      try {
        const res = await getVolunteerOrganizationNames()
        if (res.success && res.data) {
          const names = res.data
          // Map to Organization format expected by OrganizationInputField
          setOrganizations(
            names
              .filter((n: unknown): n is string => typeof n === 'string' && n.trim() !== '')
              .map((name, index) => ({ id: index, name }))
          )
        }
      } catch (error) {
        console.error('Error fetching organization names:', error)
      } finally {
        setIsLoading(false)
      }
    }
    fetchOrganizations()
  }, [])

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <OrganizationInputField
          field={field}
          fieldState={fieldState}
          organizations={organizations}
          isLoading={isLoading}
          inputGroupProps={inputGroupProps}
          placeholder={placeholder}
          disabled={isReadOnly || disabled}
          className={className}
          {...rest}
        />
      )}
    />
  )
}

export default FormOrganizationInput

